(ns practitest.frontend.users-view.views
  (:require
    [clojure.string :as str]
    [practitest.frontend.active-sessions.views :as active-sessions-views]
    [practitest.frontend.common-entities.events :as common-events]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.i18n :refer [get-translation format-translation]]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.common.re-frame :refer [partial-dispatch]]
    [practitest.frontend.common.tooltip :as common-tooltip]
    [practitest.frontend.components.grid.common :as old-gc]
    [practitest.frontend.components.grid.events :as grid.events]
    [practitest.frontend.components.grid.localstorage :as grid-storage]
    [practitest.frontend.components.grid.sessionstorage :as grid-session-storage]
    [practitest.frontend.components.grid.subs :as grid.subs]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.modal :as modal]
    [practitest.frontend.components.navigation :as navigation]
    [practitest.frontend.components.overflow :as overflow]
    [practitest.frontend.components.overlay2 :as overlay2]
    [practitest.frontend.components.selector :as selector2]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.components2.grid :as grid2]
    [practitest.frontend.components2.grid.common :as gc]
    [practitest.frontend.components2.grid.row-menu :refer [row-menu]]
    [practitest.frontend.components2.info-note :as info-note]
    [practitest.frontend.components2.modal-header :as modal-header]
    [practitest.frontend.components2.spinners :as spinners]
    [practitest.frontend.subs :as subs]
    [practitest.frontend.users-view.events :as events]
    [practitest.frontend.users-view.project-groups :as project-groups]
    [practitest.frontend.users-view.subs :as users-view-subs]
    [practitest.frontend.users-view.utils :as utils]
    [re-frame.core :as re-frame]
    [reagent.core :as reagent]))

(def current-entity :users-view)
(def state-key :users-view-index)

(defn- ellipsis-span [class text]
  [overflow/overflow-with-mux-tooltip
   {}
   (fn [props] [:span (merge props {:class class}) text])
   text])

(defn- name-cell [_ row]
  [:div.users-view-name-cell
   [ellipsis-span "users-view-name-text" (:name row)]
   (when (:is_account_owner row)
     [:span.users-view-owner-badge {:title (get-translation "account.owner.label" "Account Owner")}
      [icons/mux-gear-cutout]])])

(defn- projects-item [{:keys [item inner-ref on-hover on-change hover?]}]
  [:div.selector-item.users-view-project-item
   {:class (dom/classes-pairs :hover hover?)
    :ref inner-ref
    :on-mouse-over on-hover
    :on-mouse-move on-hover
    :on-click #(when on-change (on-change item))}
   [ellipsis-span "project-name" (:project_name item)]])

(defn- projects-cell [_ row]
  (reagent/with-let [activated? (reagent/atom false)]
    (let [projects (:projects row)
          count-val (:project_count row)]
      (cond
        (not (and count-val (pos? count-val)))
        [:span (str (or count-val 0))]

        @activated?
        [selector2/new-select
         {:items projects
          :label-fn :project_name
          :key-fn :id
          :class "users-view-projects-selector"
          :target-width? false
          :filterable? true
          :autofocus? true
          :empty-placeholder (get-translation "global-fields.no-matching-projects" "No matching projects")
          :on-change (fn [_])
          :selector-item-renderer projects-item
          :selected-item-renderer (fn [_] [:span.users-view-projects-count (str count-val)])}]

        :else
        [:span.users-view-projects-count {:on-click #(reset! activated? true)} (str count-val)]))))

;; :type :custom's text-fn always returns nil, which silently breaks sort/filter, so these get real types.
(gc/register-field-type!
  :user-name-with-owner
  gc/header-title
  (fn [{:keys [field]} row] (str (get row field)))
  name-cell)

;; Also needed in the old registry, which drives filtering.
(old-gc/register-field-type!
  :user-name-with-owner
  old-gc/header-title
  (fn [{:keys [field]} row] (str (get row field)))
  (fn [{:keys [field]} row] (str (get row field))))

(gc/register-field-type!
  :user-projects-count
  gc/header-title
  (fn [{:keys [field]} row] (str (get row field)))
  projects-cell
  gc/default-search-fn
  (fn [{:keys [field]} direction row-a row-b]
    (let [a (or (get row-a field) 0)
          b (or (get row-b field) 0)]
      (if (= direction :asc) (compare a b) (compare b a)))))

(defn- columns [full-data? show-last-active-time? pat-supported?]
  (filterv
   some?
   [{:autofilter? true
     :default-width 200
     :field :name
     :header (get-translation "users-view.column.user-name" "User Name")
     :type :user-name-with-owner}
    {:autofilter? true
     :default-width 250
     :field :email
     :header (get-translation "generic.label.email" "Email")
     :type :text}
    ;; Seat type is derived in Ruby, two different ways depending on the account's
    ;; pricing, so the server cannot order or filter by it. Offer both only while the
    ;; whole set is in the browser and the grid can do it client-side.
    {:autofilter? full-data?
     :multiple? full-data?
     :sortable? full-data?
     :default-width 150
     :field :seat_type
     :header (get-translation "users-view.column.seat-type" "Seat Type")
     :type :list
     :possible-values @(re-frame/subscribe [::users-view-subs/seat-type-options])}
    ;; Stats can't be ordered by efficiently server-side, so sort only client-side.
    (when show-last-active-time?
      {:sortable? full-data?
       :default-width 200
       :field :last_active_time
       :header (get-translation "user-activity-log.column.last-active-time" "Last Active Time")
       :type :date-time})
    {:autofilter? pat-supported?
     :multiple? true
     :sortable? pat-supported?
     :default-width 120
     :field :pat_state_label
     :header (get-translation "users-view.column.pat" "PAT")
     :type :list
     :possible-values (map utils/pat-state-label utils/pat-states)}
    {:default-width 100
     :field :project_count
     :header (get-translation "users-view.column.projects" "# Projects")
     :type :user-projects-count}
    {:autofilter? true
     :multiple? true
     :sortable? false
     :default-width 300
     :field :groups_list
     :display-field :groups_text
     :header (get-translation "users-view.column.groups" "Groups (Permissions)")
     :type :multilist
     :possible-values @(re-frame/subscribe [::users-view-subs/group-filter-options])}]))

(defn- get-row-menu-actions [row]
  (let [owner? (:is_account_owner row)
        ;; The server never removes you from your own account, so don't offer it.
        self? (= (:id row) @(re-frame/subscribe [::users-view-subs/current-user-id]))
        pat-supported? @(re-frame/subscribe [::users-view-subs/pat-for-new-users-supported?])]
    (cond-> [{:id :reset-password
              :icon icons/lock
              :label (get-translation "users-view.action.reset-password" "Reset Password")
              :on-click #(re-frame/dispatch [::events/open-modal :reset-password row])}
             {:id :edit-user-name
              :icon icons/mux-pencil-rename
              :label (get-translation "users-view.action.edit-user-name" "Edit User Name")
              :on-click #(re-frame/dispatch [::events/open-modal :rename row {:rename-value nil}])}]
      pat-supported?
      (conj {:id :edit-pat-settings
             :icon icons/api-test
             :label (get-translation "users-view.action.edit-pat-settings" "Edit PAT Settings")
             :on-click #(re-frame/dispatch [::events/open-modal :pat row {:pat-value (:pat_state row)}])})
      (not owner?)
      (conj {:id :make-owner
             :icon icons/mux-gear-cutout
             :label (get-translation "users-view.action.make-owner" "Make an Owner")
             :on-click #(re-frame/dispatch [::events/open-modal :make-owner row])})
      (and owner? (not self?))
      (conj {:id :remove-owner
             :class "users-view-remove-action"
             :icon icons/mux-gear-cutout
             :label (get-translation "users-view.action.remove-owner" "Remove Account Ownership")
             :on-click #(re-frame/dispatch [::events/open-modal :remove-owner row])})
      (not self?)
      (conj {:id :remove-user
             :class "users-view-remove-action"
             :icon icons/mux-trash
             :label (get-translation "users-view.action.remove-user" "Remove User")
             :on-click #(re-frame/dispatch [::events/open-modal :remove-user row])}))))

(defn- post-columns []
  [{:header ""
    :field :actions
    :default-width 60
    :width 60
    :type :custom
    :text-align :center
    :resizable? false
    :sortable? false
    :systemcol? true
    :class ["action-menu"]
    :cell-fn (fn [_ row] [row-menu row false (get-row-menu-actions row)])}])

(defn- account-action-modal
  "Shared shell for this page's action modals - handles the bootstrap-modal wrapper,
   class composition, and the Cancel/Confirm footer common to all of them."
  [{:keys [container-ref class title title-as body footer-note confirm-label confirm-disabled? confirm-danger? on-confirm]}]
  [modal/bootstrap-modal
   {:is-open? true
    :on-request-close #(re-frame/dispatch [::events/close-modal])
    :close-button? true
    :container-ref container-ref
    :title-as title-as
    :class (str "account-modal" (when class (str " " class)))}
   title
   body
   [:<>
    (when footer-note
      [:span.modal-footer-note footer-note])
    [buttons/button {:on-click #(re-frame/dispatch [::events/close-modal])
                     :text (get-translation "generic.button.cancel" "Cancel")}]
    [buttons/button {:primary? true
                     :danger? confirm-danger?
                     :disabled? confirm-disabled?
                     :on-click on-confirm
                     :text confirm-label}]]])

(defn- rename-modal [container-ref]
  (let [active-modal @(re-frame/subscribe [::users-view-subs/active-modal])
        value @(re-frame/subscribe [::users-view-subs/rename-value])]
    (when (= active-modal :rename)
      [account-action-modal
       {:container-ref container-ref
        :title (get-translation "users-view.rename-modal.title" "Rename User")
        :confirm-label (get-translation "generic.button.update" "Update")
        :confirm-disabled? (str/blank? value)
        :on-confirm #(re-frame/dispatch [::events/rename-user])
        :body [:div
               [:p (get-translation "users-view.rename-modal.body" "You can choose to rename the user.")]
               [:p.users-view-modal-note
                [:strong (get-translation "generic.label.note" "Note")]
                " "
                (get-translation "users-view.rename-modal.note"
                                  "that users can change their name in Personal Settings, and their personal setting will override the account-level setting.")]
               [:div.users-view-modal-field
                [:label {:for "users-view-rename-input"} (get-translation "generic.label.name" "Name")]
                [:input.form-control
                 {:id "users-view-rename-input"
                  :type "text"
                  :placeholder (get-translation "users-view.rename-modal.placeholder" "Type a new Name")
                  :value (or value "")
                  :on-change #(re-frame/dispatch [::events/set-rename-value (-> % .-target .-value)])}]]]}])))

(defn- pat-option [id state value label]
  [:div.form-check
   [:input.form-check-input
    {:type "radio"
     :id id
     :name "users-view-pat-state"
     :checked (= value state)
     :on-change #(re-frame/dispatch [::events/set-pat-value state])}]
   [:label.form-check-label {:for id} label]])

(defn- pat-modal-labels [batch? row selection-count]
  (if batch?
    {:title (format-translation
              (get-translation "users-view.batch-pat-modal.title" "Edit API Tokens for %{count} Selected Users")
              :count selection-count)
     :intro (get-translation "users-view.batch-pat-modal.body" "Please select permissions for Personal API Token:")
     :disabled (format-translation
                 (get-translation "users-view.batch-pat-modal.disable" "Disable Personal API Token for %{count} Selected Users")
                 :count selection-count)
     :enabled (format-translation
                (get-translation "users-view.batch-pat-modal.enable" "Enable %{count} Selected Users to have Personal API Token (with their own permissions)")
                :count selection-count)
     :impersonation (format-translation
                      (get-translation "users-view.batch-pat-modal.enable-with-impersonation"
                                        "Enable %{count} Selected Users to have Personal API Token to act as a different user (user impersonation) - override fields such as author_id, changed-by-id")
                      :count selection-count)}
    (let [email (:email row)]
      {:title (format-translation
                (get-translation "users-view.pat-modal.title" "Edit %{email} Personal API Token")
                :email email)
       :intro (format-translation
                (get-translation "users-view.pat-modal.body" "Please select %{email}'s permissions for Personal API Token:")
                :email email)
       :disabled (format-translation
                   (get-translation "users-view.pat-modal.disable" "Disable Personal API Token for %{email}")
                   :email email)
       :enabled (format-translation
                  (get-translation "users-view.pat-modal.enable" "Enable %{email} to have Personal API Token")
                  :email email)
       :impersonation (format-translation
                        (get-translation "users-view.pat-modal.enable-with-impersonation"
                                          "Enable %{email} to have Personal API Token to act as a different user (user impersonation) - override fields such as author_id, changed-by-id")
                        :email email)})))

(defn- pat-modal [container-ref]
  (let [active-modal @(re-frame/subscribe [::users-view-subs/active-modal])
        row @(re-frame/subscribe [::users-view-subs/modal-row])
        value @(re-frame/subscribe [::users-view-subs/pat-value])
        selection-count (count @(re-frame/subscribe [::grid.subs/selection utils/selection-path]))]
    (when (contains? #{:pat :batch-pat} active-modal)
      (let [batch? (= active-modal :batch-pat)
            {:keys [title intro disabled enabled impersonation]} (pat-modal-labels batch? row selection-count)]
        [account-action-modal
         {:container-ref container-ref
          :class "users-view-pat-modal"
          :title title
          :confirm-label (get-translation "users-view.pat-modal.update-permissions" "Update Permissions")
          ;; The selected users can sit on different PAT states, so the batch flow
          ;; preselects nothing and waits for a choice.
          :confirm-disabled? (and batch? (str/blank? value))
          :on-confirm #(re-frame/dispatch (if batch? [::events/batch-update-pat] [::events/update-pat]))
          :body [:div
                 [:p intro]
                 [pat-option "users-view-pat-disabled" "disabled" value disabled]
                 [pat-option "users-view-pat-enabled" "enabled" value enabled]
                 [pat-option "users-view-pat-impersonation" "enabled_with_impersonation" value impersonation]]}]))))

(defn- remove-users-modal-labels [batch? row user-count]
  (if batch?
    {:title (format-translation
              (get-translation "users-view.remove-users.title" "Remove %{count} Selected Users")
              :count user-count)
     :body (get-translation "users-view.remove-users.body"
                             "The selected users will be removed from all projects in this account, along with their account ownership. Their PractiTest user is not deleted.")}
    {:title (format-translation
              (get-translation "users-view.remove-user.title" "Remove %{name}")
              :name (:name row))
     :body (format-translation
             (get-translation "users-view.remove-user.body"
                               "%{name} will be removed from all projects in this account, along with their account ownership. Their PractiTest user is not deleted.")
             :name (:name row))}))

(defn- remove-users-modal-content
  [_container-ref _batch? _title _body _user-ids]
  (let [confirm-value (reagent/atom "")
        remove-confirm-word "remove"]
    (fn [container-ref batch? title body user-ids]
      [account-action-modal
       {:container-ref container-ref
        :title title
        :confirm-label (get-translation "users-view.remove-users.confirm" "Yes, Remove")
        :confirm-danger? true
        :confirm-disabled? (and batch? (not= remove-confirm-word (str/lower-case (str/trim @confirm-value))))
        :on-confirm #(re-frame/dispatch [::events/remove-users user-ids])
        :body (if batch?
                [:div
                 [:p body]
                 [:div.users-view-modal-field
                  [:label {:for "users-view-remove-confirm-input"}
                   (get-translation "users-view.remove-users.type-to-confirm" "Type remove to confirm")]
                  [:input.form-control.users-view-remove-confirm-input
                   {:id "users-view-remove-confirm-input"
                    :type "text"
                    :placeholder (get-translation "users-view.remove-users.type-placeholder" "Type remove")
                    :value @confirm-value
                    :on-change #(reset! confirm-value (-> % .-target .-value))}]]]
                [:p body])}])))

(defn- remove-users-modal [container-ref]
  (let [active-modal @(re-frame/subscribe [::users-view-subs/active-modal])
        row @(re-frame/subscribe [::users-view-subs/modal-row])
        current-user-id @(re-frame/subscribe [::users-view-subs/current-user-id])
        selection @(re-frame/subscribe [::grid.subs/selection utils/selection-path])]
    (when (contains? #{:remove-user :batch-remove-users} active-modal)
      (let [batch? (= active-modal :batch-remove-users)
            ;; The server skips you, so leave yourself out of both the count and the request.
            user-ids (if batch? (disj (set selection) current-user-id) [(:id row)])
            {:keys [title body]} (remove-users-modal-labels batch? row (count user-ids))]
        [remove-users-modal-content container-ref batch? title body user-ids]))))

(defn- make-owner-modal [container-ref]
  (let [active-modal @(re-frame/subscribe [::users-view-subs/active-modal])
        row @(re-frame/subscribe [::users-view-subs/modal-row])
        ;; With a single owner allowed (Team plan), making someone an owner transfers it from you.
        single-owner? (not @(re-frame/subscribe [::users-view-subs/multiple-owners-supported?]))]
    (when (= active-modal :make-owner)
      [account-action-modal
       {:container-ref container-ref
        :class "users-view-make-owner-modal"
        :title (format-translation
                 (get-translation "users-view.make-owner.title" "Make %{name} an account owner")
                 :name (:name row))
        :confirm-label (if single-owner?
                         (get-translation "users-view.make-owner.confirm-replace" "Yes, Replace an Owner")
                         (get-translation "users-view.make-owner.confirm" "Yes, Make an Owner"))
        :confirm-danger? single-owner?
        :on-confirm #(re-frame/dispatch [::events/add-owner])
        :body (if single-owner?
                [:div
                 [:p (format-translation
                       (get-translation "users-view.make-owner.body-intro-team"
                                         "Since you're on a Team plan (only 1 owner allowed), ownership will be transferred from you to %{name} and they will be able to do many things like:")
                       :name (:name row))]
                 [:ul
                  [:li (get-translation "users-view.make-owner.body-change-ownership"
                                         "Change Ownership of the account")]
                  [:li (get-translation "users-view.make-owner.body-delete-projects"
                                         "Permanently Delete Projects from PractiTest.")]
                  [:li (get-translation "users-view.make-owner.body-manage-users"
                                         "Manage users in the account")]
                  [:li (get-translation "users-view.make-owner.body-api-tokens"
                                         "Create and view API tokens that have full access rights.")]]]
                [:div
                 [:p (format-translation
                       (get-translation "users-view.make-owner.body-intro"
                                         "%{name} will be an account owner (just like you!) and will be able to do many things such as:")
                       :name (:name row))]
                 [:ul
                  [:li (get-translation "users-view.make-owner.body-remove-you"
                                         "Remove you from the account owners list (so you won't be able to get back here).")]
                  [:li (get-translation "users-view.make-owner.body-delete-projects"
                                         "Permanently Delete Projects from PractiTest.")]
                  [:li (get-translation "users-view.make-owner.body-api-tokens"
                                         "Create and view API tokens that have full access rights.")]]])}])))

(defn- remove-owner-modal [container-ref]
  (let [active-modal @(re-frame/subscribe [::users-view-subs/active-modal])
        row @(re-frame/subscribe [::users-view-subs/modal-row])]
    (when (= active-modal :remove-owner)
      [account-action-modal
       {:container-ref container-ref
        :title (format-translation
                 (get-translation "users-view.remove-owner.title" "Remove %{name} from Account Ownership")
                 :name (:name row))
        :confirm-label (get-translation "users-view.remove-owner.confirm" "Yes, Remove Ownership")
        :confirm-danger? true
        :on-confirm #(re-frame/dispatch [::events/remove-owner])
        :body [:p (get-translation "users-view.remove-owner.body"
                                    "This user will no longer be an account owner. Note that removing account ownership does not remove them from the projects themselves.")]}])))

(defn- reset-password-modal [container-ref]
  (let [active-modal @(re-frame/subscribe [::users-view-subs/active-modal])]
    (when (= active-modal :reset-password)
      (let [label (get-translation "users-view.action.reset-password" "Reset Password")]
        [account-action-modal
         {:container-ref container-ref
          :title label
          :confirm-label label
          :on-confirm #(re-frame/dispatch [::events/reset-password])
          :body [:p (get-translation "users-view.reset-password.body"
                                      "When you click on \"Reset password\" below, an email with password reset link and validation code will be sent to the user")]}]))))

(defn- invite-field [label info input]
  [:div.invite-field
   [:label.invite-label
    label
    (when info
      [common-tooltip/info-circle-tooltip info {:class "info-icon"}])]
   [:div.invite-control
    input]])

(defn- invite-email-field [email email-error]
  [invite-field
   (get-translation "users-view.new-user.email" "Email Address")
   (get-translation "users-view.new-user.email-info" "Separate multiple emails with commas")
   [:<>
    [:input.form-control
     {:type "text"
      :value email
      :on-change #(re-frame/dispatch [::events/set-invite-field :email (dom/event-value %)])}]
    (when email-error
      [:span.invite-error email-error])]])

(defn- invite-name-field [email name emails]
  (let [multiple? (> (count emails) 1)]
    [invite-field
     (get-translation "generic.label.display-name" "Display Name")
     (get-translation "users-view.new-user.name-info"
                      "Defaults to the email address. The user can change it later in Personal Settings.")
     [:input.form-control
      {:type "text"
       :disabled multiple?
       :placeholder (when multiple? (str/join ", " emails))
       :value (if multiple? "" (or name email))
       :on-change #(re-frame/dispatch [::events/set-invite-field :name (dom/event-value %)])}]]))

(defn- invite-projects-section [seat-type]
  [:<>
   [:div.invite-section
    [:h4.invite-section-title
     (get-translation "users-view.new-user.select-projects" "Select Projects")]
    [:p.invite-helper
     (get-translation "users-view.new-user.projects-helper"
                      "A user can belong to many projects, each with a different group.")
     " "
     [:a {:href "#" :target "_blank" :rel "noopener noreferrer"}
      (get-translation "users-view.new-user.learn-how" "Learn how")]]]
   [invite-field
    (get-translation "users-view.column.seat-type" "Seat Type")
    (get-translation "users-view.new-user.seat-type-info"
                     "Set automatically from the selected groups. It affects the licenses your account is charged for.")
    [:input.form-control
     {:type "text"
      :disabled true
      :value (or seat-type "")}]]
   [project-groups/project-groups]])

(defn- new-user-modal [container-ref]
  (let [active-modal @(re-frame/subscribe [::users-view-subs/active-modal])
        {:keys [email name error] :as form} @(re-frame/subscribe [::users-view-subs/invite-form])
        emails @(re-frame/subscribe [::users-view-subs/invite-emails])
        seat-type @(re-frame/subscribe [::users-view-subs/invite-seat-type])
        projects-count @(re-frame/subscribe [::users-view-subs/assigned-projects-count])
        email-error (utils/invite-email-error emails form)]
    (when (= active-modal :new-user)
      [account-action-modal
       {:container-ref container-ref
        :class "invite-modal"
        :title-as "div"
        :title [modal-header/modal-header
                {:icon [icons/mux-reg-plus]
                 :title (get-translation "users-view.new-user.title" "Add New User")
                 :context (get-translation "users-view.new-user.title-suffix" "Invite to Account")
                 :subtitle (get-translation "users-view.new-user.subtitle"
                                            "Invite a user by email and assign them to at least one project.")}]
        :confirm-label (get-translation "users-view.new-user.confirm" "Add user")
        :confirm-disabled? (not (and (seq emails)
                                     (nil? email-error)
                                     (utils/project-groups-complete? form)))
        :on-confirm #(re-frame/dispatch [::events/invite-user seat-type])
        :footer-note (format-translation
                       (get-translation "users-view.new-user.summary" "Adds %{users} user(s) - reaching %{count} project(s)")
                       :users (count emails)
                       :count projects-count)
        :body [:div.invite-body
               [invite-email-field email email-error]
               [invite-name-field email name emails]
               [invite-projects-section seat-type]
               (when error
                 [:p.invite-error error])
               [info-note/mux-info-note
                {:class "invite-note"
                 :message (get-translation "users-view.new-user.info-note"
                                           "The user receives an email invitation. A seat is consumed only once they accept and sign in.")}]]}])))

(defn- add-to-projects-header []
  [modal-header/modal-header
   {:icon [icons/mux-reg-plus]
    :title (get-translation "users-view.add-to-projects.title" "Add To Projects")
    :context (get-translation "users-view.add-to-projects.title-suffix" "Invite to Projects")
    :subtitle (get-translation "users-view.add-to-projects.subtitle"
                               "Invite a user by email and assign them to additional projects.")}])

(defn- selected-users-chip [user-names]
  [:div.selected-users-chip
   (format-translation
     (get-translation "users-view.add-to-projects.selected-users" "%{count} Users Selected · %{names}")
     :count (count user-names)
     :names (str/join "; " user-names))])

(defn- add-to-projects-body [user-names error]
  [:div.invite-body
   [selected-users-chip user-names]
   [:p.add-to-projects-label
    (get-translation "users-view.add-to-projects.select-projects" "Select the project(s) to add them to:")]
   [project-groups/project-groups]
   (when error
     [:p.invite-error error])])

(defn- add-to-projects-modal [container-ref]
  (let [active-modal @(re-frame/subscribe [::users-view-subs/active-modal])
        {:keys [error] :as form} @(re-frame/subscribe [::users-view-subs/invite-form])
        user-names @(re-frame/subscribe [::users-view-subs/selected-user-names])
        projects-count @(re-frame/subscribe [::users-view-subs/assigned-projects-count])]
    (when (= active-modal :add-to-projects)
      [account-action-modal
       {:container-ref container-ref
        :class "invite-modal"
        :title-as "div"
        :title [add-to-projects-header]
        :confirm-label (get-translation "generic.button.save" "Save")
        :confirm-disabled? (not (utils/project-groups-complete? form))
        :on-confirm #(re-frame/dispatch [::events/add-users-to-projects])
        :footer-note (format-translation
                       (get-translation "users-view.new-user.summary" "Adds %{users} user(s) - reaching %{count} project(s)")
                       :users (count user-names)
                       :count projects-count)
        :body [add-to-projects-body user-names error]}])))

(defn- export-button []
  [buttons/button
   {:class "users-view-export-button"
    :icon [icons/mux-external-link]
    :text (get-translation "users-view.export.button" "Export")
    :on-click (fn []
                (re-frame/dispatch [::events/track-event "Export List"])
                (re-frame/dispatch [::events/export-users]))}])

(defn- batch-menu-item [hide! {:keys [class text icon mixpanel-action modal modal-state
                                      disabled? disabled-tooltip-text]}]
  [navigation/menu-item-element
   {:class class
    :text text
    :icon icon
    :disabled? disabled?
    :disabled-tooltip? disabled?
    :disabled-tooltip-text disabled-tooltip-text
    :on-click (fn []
                (mixpanel/track "Grid Actions" {"Action" mixpanel-action})
                (hide!)
                (re-frame/dispatch [::events/open-modal modal nil modal-state]))}])

;; Same menu as the grid's default one, with this page's batch actions above Edit columns.
(defn- users-view-more-button [_]
  (let [local-state (reagent/atom {})]
    (fn [grid-props]
      (let [show? (:show-toolbar-menu? @local-state)
            hide! #(swap! local-state assoc :show-toolbar-menu? false)
            toggle! #(swap! local-state update :show-toolbar-menu? not)
            selection @(re-frame/subscribe [::grid.subs/selection utils/selection-path])
            no-selection? (empty? selection)
            no-selection-text (get-translation "users-view.batch-actions.no-selection"
                                                "Select at least one user first")
            ;; The server never removes you, so a selection of just yourself has nothing to remove.
            removable (disj (set selection) @(re-frame/subscribe [::users-view-subs/current-user-id]))]
        [overlay2/floating-ui-overlay
         {:placement "bottom-end"
          :strategy "absolute"
          :offset 5
          :show? show?
          :on-request-close hide!}
         [:button.more-button
          {:on-click toggle!}
          [icons/mux-three-dots]]
         [:div.menu-popup
          (when @(re-frame/subscribe [::users-view-subs/pat-for-new-users-supported?])
            [batch-menu-item hide!
             {:text (get-translation "users-view.action.edit-pat-settings" "Edit PAT Settings")
              :icon icons/api-test
              :mixpanel-action "Batch Edit PAT Settings"
              :modal :batch-pat
              :modal-state {:pat-value nil}
              :disabled? no-selection?
              :disabled-tooltip-text no-selection-text}])
          [batch-menu-item hide!
           {:class "users-view-remove-action"
            :text (get-translation "users-view.action.remove-user" "Remove User")
            :icon icons/mux-trash
            :mixpanel-action "Batch Remove User"
            :modal :batch-remove-users
            :disabled? (empty? removable)
            :disabled-tooltip-text (if no-selection?
                                     no-selection-text
                                     (get-translation "users-view.remove-users.self-only"
                                                       "You cannot remove yourself from the account"))}]
          [grid2/edit-columns-menu-item grid-props]]]))))

(defn- new-user-button []
  [buttons/button
   {:class "new-user-button"
    :primary? true
    :icon [icons/mux-plus-circle]
    :text (get-translation "users-view.toolbar.new-user" "New User")
    :on-click #(re-frame/dispatch [::events/open-invite-modal :new-user])}])

(defn- add-to-project-button []
  (let [no-selection? (empty? @(re-frame/subscribe [::grid.subs/selection utils/selection-path]))]
    [buttons/button
     {:class "add-to-project-button"
      :icon [icons/mux-link2]
      :text (get-translation "users-view.toolbar.add-to-project" "Add To Project")
      :disabled? no-selection?
      :tooltip (when no-selection?
                 (get-translation "users-view.batch-actions.no-selection" "Select at least one user first"))
      :on-click #(re-frame/dispatch [::events/open-invite-modal :add-to-projects])}]))

(defn- users-view-toolbar [grid-props]
  [:div.users-view-toolbar
   [new-user-button]
   [add-to-project-button]
   [export-button]
   [users-view-more-button grid-props]])

(defn users-view-grid [grid-state]
  (let [table-id utils/table-id
        full-data? @(re-frame/subscribe [::users-view-subs/full-data?])
        users @(re-frame/subscribe [::users-view-subs/rows])
        grid-props {:columns (columns full-data?
                                      @(re-frame/subscribe [::users-view-subs/show-last-active-time?])
                                      @(re-frame/subscribe [::users-view-subs/pat-for-new-users-supported?]))
                    :post-columns (post-columns)
                    :table-id table-id
                    :grid-state grid-state
                    :local-state-updating grid-state
                    :full-data? full-data?
                    :loading? @(re-frame/subscribe [::subs/progress? ::events/refresh-users])
                    :current-page @(re-frame/subscribe [::users-view-subs/current-page])
                    :total-count @(re-frame/subscribe [::users-view-subs/total-count])
                    :on-query-changed #(re-frame/dispatch [::events/refresh-users %])
                    ;; A filter change re-queries page 1, but the grid keeps its own page
                    ;; number, so reset it too or the pager points at the wrong page.
                    :on-filter-changed (fn []
                                         (swap! grid-state assoc :current-page 1)
                                         (grid-storage/save-current-page table-id 1))
                    :selection @(re-frame/subscribe [::grid.subs/selection utils/selection-path])
                    :selected? #(re-frame/subscribe [::grid.subs/selected? utils/selection-path %])
                    :all-selected? #(re-frame/subscribe [::grid.subs/all-selected? utils/selection-path %])
                    :toggle-selection (partial-dispatch [::grid.events/toggle-selection utils/selection-path])
                    :toggle-selection-page (partial-dispatch [::grid.events/toggle-selection-page utils/selection-path])
                    :clear-selection (partial-dispatch [::grid.events/clear-selection utils/selection-path])
                    :show-edit-columns? true
                    :grid-toolbar users-view-toolbar
                    :expanded? @(re-frame/subscribe [::grid.subs/expanded? current-entity state-key])
                    :on-expand-click (partial-dispatch [::common-events/toggle-expanded current-entity state-key])
                    :current-page-load     (reagent/partial grid-storage/load-current-page table-id)
                    :current-page-save     (reagent/partial grid-storage/save-current-page table-id)
                    :per-page-save         (reagent/partial grid-storage/save-per-page table-id)
                    :per-page-load         (reagent/partial grid-storage/load-per-page table-id)
                    :sort-order-save       (reagent/partial grid-session-storage/save-sort-order table-id)
                    :sort-order-load       (reagent/partial grid-session-storage/load-sort-order table-id)
                    :data users
                    :class "admin-page-table"}]
    [:div.users-view-grid
     [grid2/grid grid-props]]))

(defn- footer-checkbox [{:keys [id checked? label state-key route mixpanel-action]}]
  [:div.m-checkbox-container
   [:label.m-checkbox-label
    [:input.m-checkbox
     {:type "checkbox"
      :id id
      :checked checked?
      :on-change (fn []
                   (re-frame/dispatch [::events/track-event
                                       (str mixpanel-action (if checked? " Disabled" " Enabled"))])
                   (re-frame/dispatch [::events/toggle-account-setting state-key route (not checked?)]))}]
    label]])

(defn- seats-usage-text [seats-usage]
  (->> seats-usage
       (map (fn [[label value]] (str label ": " value)))
       (str/join " , ")))

(defn- seats-footer []
  (let [seats-usage @(re-frame/subscribe [::users-view-subs/seats-usage])]
    [:div.users-view-footer
     [:div.users-view-footer-seats
      [:strong (get-translation "users-view.footer.seats-usage" "Seats usage in account")]
      ": "
      (seats-usage-text seats-usage)
      " | "
      (get-translation "users-view.footer.billing-note" "Only users for billing (from enabled projects) are listed")]

     (when @(re-frame/subscribe [::users-view-subs/notify-license-change-supported?])
       [footer-checkbox
        {:id "users-view-notify-license-change"
         :checked? @(re-frame/subscribe [::users-view-subs/notify-license-change?])
         :state-key :notify-license-change?
         :route "account/accounts#toggle_license_change_notify"
         :mixpanel-action "Account Notifications"
         :label (get-translation "users-view.footer.account-notifications"
                                  "Account Notifications - Checking this will Notify account owners when a new license is used")}])

     (when @(re-frame/subscribe [::users-view-subs/pat-for-new-users-supported?])
       [footer-checkbox
        {:id "users-view-pat-for-new-users"
         :checked? @(re-frame/subscribe [::users-view-subs/pat-for-new-users?])
         :state-key :pat-for-new-users?
         :route "account/accounts#toggle_pat_for_new_users"
         :mixpanel-action "Auto-enable PAT"
         :label (get-translation "users-view.footer.auto-enable-pat"
                                  "Auto-enable Personal API Token for new users added to this account")}])]))

(defn- users-view-tab [active-tab tab-id label mixpanel-action]
  [:li.nav-item
   [:a.nav-link
    {:href "#"
     :class (dom/classes-pairs :active (= tab-id active-tab))
     :on-click (fn [e]
                 (.preventDefault e)
                 (re-frame/dispatch [::events/track-event mixpanel-action])
                 (re-frame/dispatch [::events/set-active-tab tab-id]))}
    label]])

(defn- users-view-tabs [active-tab]
  [:ul.nav.nav-tabs.users-view-tabs
   [users-view-tab active-tab :users-view (get-translation "users-view.tab.users-view" "Users View")
    "Tab Users By Projects Opened"]
   [users-view-tab active-tab :active-sessions (get-translation "active-sessions.tab.title" "Active Sessions")
    "Tab Active Sessions Opened"]])

(defn main-panel []
  (let [grid-state (reagent/atom {:module "users-view"})
        container-ref (atom nil)]
    (fn []
      (let [concurrent-licensing? @(re-frame/subscribe [::users-view-subs/concurrent-licensing?])
            active-tab (if concurrent-licensing?
                         @(re-frame/subscribe [::users-view-subs/active-tab])
                         :users-view)]
        [:div.users-view-page
         {:ref #(reset! container-ref %)}
         (when concurrent-licensing?
           [users-view-tabs active-tab])
         (if (= active-tab :active-sessions)
           [active-sessions-views/main-panel]
           [:<>
            [users-view-grid grid-state]
            [seats-footer]])
         [rename-modal container-ref]
         [pat-modal container-ref]
         [remove-users-modal container-ref]
         [make-owner-modal container-ref]
         [remove-owner-modal container-ref]
         [reset-password-modal container-ref]
         [new-user-modal container-ref]
         [add-to-projects-modal container-ref]]))))

(defn users-view-page []
  (if @(re-frame/subscribe [::users-view-subs/loading?])
    [:div.centered-spinner [spinners/mux-spinner {:size :modal}]]
    [main-panel]))

(def main-controller
  {:start (fn [_params]
            (re-frame/dispatch [::events/load!]))})
